import { useEffect } from 'react';

import { CloseOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Card } from 'antd';
import { FormProvider, useForm } from 'react-hook-form';

import { useRejectTestingParticipantMutation } from '@/api/endpoints/schools/schoolSelectionPanel';
import { DataSection, TextAreaField } from '@/shared/components';
import { CheckboxField } from '@/shared/components/Fields/CheckboxField';
import { useApiRequest } from '@/shared/hooks';

import { REJECT_PARTICIPANT_VALIDATION_SCHEMA } from './consts';
import styles from './RejectParticipantSection.module.scss';
import type { IFormData } from './types';

interface IProp {
  handleCancel: VoidFunction;
  rejectionReason: string | null;
  isRejectedBySecurityService: boolean;
  participantId: number;
  schoolId: number;
  setIsModalLoading: (state: boolean) => void;
}
export const RejectParticipantSection = ({
  handleCancel,
  rejectionReason,
  isRejectedBySecurityService,
  participantId,
  schoolId,
  setIsModalLoading,
}: IProp) => {
  const [rejectParticipant, { isLoading: isParticipantRejecting }] = useRejectTestingParticipantMutation();

  const sendApiRequest = useApiRequest();

  const methods = useForm<IFormData>({
    defaultValues: {
      rejectionReason: rejectionReason ?? '',
      isRejectedBySecurityService,
    },
    resolver: yupResolver(REJECT_PARTICIPANT_VALIDATION_SCHEMA),
  });

  const { handleSubmit, reset } = methods;

  const rejectParticipantHandler = handleSubmit(async ({ rejectionReason, isRejectedBySecurityService }) => {
    setIsModalLoading(true);

    await sendApiRequest({
      successMessage: 'Данные успешно сохранены',
      apiRequestCallback: () =>
        rejectParticipant({
          schoolId: Number(schoolId),
          participantId,
          rejectionReason,
          isUserBlocked: isRejectedBySecurityService,
        }),
      successCallback: () => {
        handleCancel();
      },
    });

    setIsModalLoading(false);
  });

  useEffect(() => {
    reset({
      rejectionReason: rejectionReason ?? '',
      isRejectedBySecurityService,
    });
  }, [rejectionReason, isRejectedBySecurityService]);

  return (
    <FormProvider {...methods}>
      <div className={styles.wrapper}>
        <div className={styles['form-field-wrapper']}>
          <DataSection
            titleText="Причина отклонения"
            isTitleRequired
          >
            <TextAreaField
              fieldName="rejectionReason"
              placeholder="Например: Не предоставлены необходимые документы..."
            />
          </DataSection>
          <p className={styles['field-annotation']}>Эта причина будет отправлена участнику в уведомлении</p>
        </div>
        <Card className={styles['security-service-block-card']}>
          <CheckboxField
            className={styles['security-service-checkbox-field']}
            fieldName="isRejectedBySecurityService"
          >
            Отклонить по причине непрохождения проверки СБ
          </CheckboxField>
          <p className={styles['security-service-block-annotation']}>
            При активации этой опции участник получит блокировку на&nbsp;подачу заявок в&nbsp;будущем. Участник
            НЕ&nbsp;будет знать о&nbsp;блокировке и&nbsp;увидит только указанную выше причину отклонения.
          </p>
        </Card>
        <Card className={styles['final-annotation-card']}>
          <p className={styles['final-annotation']}>
            <strong className={styles['final-annotation-strong']}>Важно:</strong> Это действие нельзя отменить. Участник
            будет навсегда заблокирован в&nbsp;системе, но&nbsp;получит указанную выше причину отклонения (например:
            &laquo;Не&nbsp;предоставлены необходимые документы&raquo; или другую, которую вы&nbsp;укажете).
          </p>
        </Card>
      </div>
      <div className={styles['buttons-wrapper']}>
        <Button
          className={styles.button}
          onClick={handleCancel}
        >
          Отмена
        </Button>
        <Button
          variant="solid"
          color="danger"
          icon={<CloseOutlined />}
          className={styles.button}
          onClick={rejectParticipantHandler}
          disabled={isParticipantRejecting}
        >
          Отклонить участника
        </Button>
      </div>
    </FormProvider>
  );
};
